import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { btnStyle } from './theme';
import ProxyJobList from './ProxyJobList';
import DriveUploadSection from './DriveUploadSection';

export default function CompletionView({ c, manifest, onStartAnother }) {
  const ok = manifest.status === 'completed';
  const cancelled = manifest.status === 'cancelled';

  const [proxyRoot, setProxyRoot] = useState(`${manifest.destPath}/_proxies`);
  const [proxyJobs, setProxyJobs] = useState(null); // null = not started
  const [proxyError, setProxyError] = useState(null);

  // Proxy jobs are persisted in SQLite and keep running/finishing in the
  // main process independent of this window -- reconnect to any jobs
  // already queued for this ingest's proxy folder on mount, instead of
  // always showing "Generate Proxies" again and losing visibility into a
  // batch that's still going (or already finished) after a reopen/remount.
  useEffect(() => {
    let cancelled2 = false;
    (async () => {
      const queue = await window.electronAPI.proxyQueue.getQueue();
      if (cancelled2 || !queue) return;
      const relevant = queue.filter(j => j.outputPath && j.outputPath.startsWith(proxyRoot));
      if (relevant.length) setProxyJobs(relevant);
    })();
    return () => { cancelled2 = true; };
  }, [proxyRoot]);

  useEffect(() => {
    if (!proxyJobs) return;
    const offProgress = window.electronAPI.proxyQueue.onProgress(() => {}); // reserved for future per-job byte progress
    const offJobStarted = window.electronAPI.proxyQueue.onJobStarted((job) => {
      setProxyJobs((prev) => prev && prev.map(j => (j.id === job.id ? job : j)));
    });
    const offJobComplete = window.electronAPI.proxyQueue.onJobComplete((job) => {
      setProxyJobs((prev) => prev && prev.map(j => (j.id === job.id ? job : j)));
    });
    return () => { offProgress(); offJobStarted(); offJobComplete(); };
  }, [proxyJobs]);

  const chooseProxyRoot = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.chooseDestination();
    if (result?.success && result.path) setProxyRoot(result.path);
  }, []);

  const generateProxies = useCallback(async () => {
    setProxyError(null);
    const verifiedFiles = manifest.files.filter(f => f.verified).map(f => ({ relativePath: f.relativePath, size: f.size }));
    const result = await window.electronAPI.proxyQueue.enqueue(verifiedFiles, proxyRoot, manifest.destPath);
    if (result?.success) setProxyJobs(result.data);
    else setProxyError(result?.error || 'Failed to start proxy generation');
  }, [manifest, proxyRoot]);

  const proxyDone = proxyJobs ? proxyJobs.filter(j => j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled').length : 0;
  const proxyFailed = proxyJobs ? proxyJobs.filter(j => j.status === 'failed').length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {ok ? <CheckCircle2 size={20} color={c.ok} /> : <AlertTriangle size={20} color={cancelled ? c.warn : c.err} />}
        <span style={{ fontSize: '14px', fontWeight: 700 }}>
          {ok ? 'Ingest complete — all files verified' : cancelled ? 'Ingest cancelled' : 'Ingest completed with errors'}
        </span>
      </div>
      <div style={{ fontSize: '12px', color: c.muted }}>
        {manifest.filesProcessed}/{manifest.fileCount} files processed
        {manifest.failedCount > 0 && `, ${manifest.failedCount} failed`}
      </div>
      {manifest.failedCount > 0 && (
        <div style={{ padding: '10px 14px', border: `1px solid ${c.err}`, borderRadius: '8px', fontSize: '11px', maxHeight: '160px', overflow: 'auto' }}>
          {manifest.files.filter(f => !f.verified).map(f => (
            <div key={f.relativePath}>{f.relativePath}: {f.error}</div>
          ))}
        </div>
      )}
      <div style={{ padding: '10px 14px', border: `1px solid ${c.rule}`, borderRadius: '8px', fontSize: '12px', background: c.subtleBg }}>
        Nothing was deleted from the card — safe to eject once you&apos;ve reviewed this summary.
        Manifest and report saved to <code>{manifest.destPath}/_ingest/</code>.
      </div>

      {/* Proxy generation (Phase 4) */}
      <div style={{ padding: '12px 14px', border: `1px solid ${c.rule}`, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600 }}>Proxies</div>
        {!proxyJobs && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ fontSize: '11px', color: c.muted, fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{proxyRoot}</div>
              <button onClick={chooseProxyRoot} style={btnStyle(c, 'ghost')}>Change</button>
            </div>
            <button onClick={generateProxies} style={{ ...btnStyle(c, 'primary'), alignSelf: 'flex-start' }}>
              Generate Proxies
            </button>
            {proxyError && <div style={{ fontSize: '11px', color: c.err }}>{proxyError}</div>}
          </>
        )}
        {proxyJobs && (
          <>
            <div style={{ height: '8px', borderRadius: '4px', background: c.rule, overflow: 'hidden' }}>
              <div style={{
                height: '100%', background: c.accent, transition: 'width 0.2s',
                width: `${proxyJobs.length ? Math.round((proxyDone / proxyJobs.length) * 100) : 0}%`,
              }} />
            </div>
            <div style={{ fontSize: '11px', color: c.muted }}>
              {proxyDone}/{proxyJobs.length} proxies done{proxyFailed > 0 ? ` · ${proxyFailed} failed` : ''}
            </div>
            <ProxyJobList c={c} jobs={proxyJobs} />
          </>
        )}
      </div>

      <DriveUploadSection c={c} manifest={manifest} />

      <button onClick={onStartAnother} style={{ ...btnStyle(c, 'default'), alignSelf: 'flex-start' }}>
        Ingest another card
      </button>
    </div>
  );
}
